import { useSortable } from "@dnd-kit/sortable";
import { Row } from "@tanstack/react-table";
import classNames from "classnames";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { useIsElementVisible } from "PFCore/hooks/use_is_element_visible";
import { CSSProperties, ReactNode, RefObject, useMemo } from "react";

import { InteractiveTableCell } from "../interactive/interactive_table_cell";
import { IsRowDragDisabledFn } from "../reorder";
import { RowDragHandleContext } from "../reorder/row_drag_handle_context";
import { RowSelectionConditions } from "../selection";
import { TableData } from "../table";
import css from "../table.module.scss";
import { OnRowClickFn } from "../table.types";
import { TableCell } from "./table_cell";

type TableRowProps<TABLE_DATA extends TableData> = {
  row: Row<TABLE_DATA>;
  rowIndex: number;
  onRowClick?: OnRowClickFn<TABLE_DATA>;
  getRowClassName?: (row: Row<TABLE_DATA>, rowIndex: number) => string | undefined;
  compact?: boolean;
  topAlignContent?: boolean;
  noHover?: boolean;
  displayGrid?: boolean;
  lastRowRef?: RefObject<HTMLTableRowElement | null>;
  renderExpandedRow?: (row: TABLE_DATA) => ReactNode;
  rowSelectionConditions?: RowSelectionConditions<TABLE_DATA>;
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  hideDistantRows?: boolean;
  isInteractive?: boolean;
  enableRowReordering?: boolean;
  isRowDragDisabled?: IsRowDragDisabledFn<TABLE_DATA>;
};

export const TableRow = <TABLE_DATA extends TableData>({
  row,
  rowIndex,
  onRowClick,
  getRowClassName,
  compact,
  topAlignContent,
  noHover,
  lastRowRef,
  renderExpandedRow,
  rowSelectionConditions,
  displayGrid,
  scrollableContainerRef,
  hideDistantRows,
  isInteractive,
  enableRowReordering,
  isRowDragDisabled
}: TableRowProps<TABLE_DATA>) => {
  const { disabled, message } = row.original;
  const isExpandable = rowSelectionConditions?.isRowExpandable?.(row);
  const isDragDisabled = !enableRowReordering || !!isRowDragDisabled?.(row);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: isDragDisabled
  });

  const dragHandleContextValue = useMemo(() => ({ attributes, listeners }), [attributes, listeners]);

  const { isVisible, elementRef } = useIsElementVisible({
    scrollableContainerRef,
    active: hideDistantRows ?? false
  });

  const CellComponent = isInteractive ? InteractiveTableCell : TableCell;

  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  const rowStyle: CSSProperties = {
    cursor: onRowClick ? "pointer" : "default",
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition
  };

  return (
    <>
      <Tooltip
        content={
          <Typography variant="bodyRegular" tag="span">
            {message}
          </Typography>
        }
        disabled={!message}
        placement="top-start"
        triggerRef={lastRowRef}
      >
        {({ ref, getReferenceProps }) => (
          <tr
            ref={(element) => {
              elementRef.current = element;
              setNodeRef(element);
              if (typeof ref === "function") {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
            }}
            {...getReferenceProps({
              className: classNames(css.row, css.rowBody, getRowClassName?.(row, rowIndex), {
                [css.noHover]: noHover,
                [css.topAlignContent]: topAlignContent,
                [css.dragging]: isDragging,
                [css.semitransparent]: isDragging
              }),
              style: rowStyle,
              onClick: handleRowClick
            })}
          >
            <RowDragHandleContext.Provider value={dragHandleContextValue}>
              {isVisible || !hideDistantRows
                ? row
                    .getVisibleCells()
                    .filter((cell) => {
                      // Skip cells absorbed by a preceding rowSpan merge.
                      const { isRowSpanHidden } = cell.column.columnDef.meta || {};
                      return !isRowSpanHidden?.(rowIndex);
                    })
                    .map((cell, visibleIndex, cells) => (
                      <CellComponent
                        key={cell.id}
                        cell={cell}
                        compact={compact}
                        displayGrid={displayGrid}
                        disabled={disabled}
                        hideDistantRows={hideDistantRows}
                        rowIndex={rowIndex}
                        colIndex={cell.column.getIndex()}
                        isLastColumn={visibleIndex === cells.length - 1}
                      />
                    ))
                : row
                    .getVisibleCells()
                    .filter((cell) => {
                      const { isRowSpanHidden } = cell.column.columnDef.meta || {};
                      return !isRowSpanHidden?.(rowIndex);
                    })
                    .map((cell) => {
                      const { getRowSpan } = cell.column.columnDef.meta || {};
                      const rowSpan = getRowSpan ? getRowSpan(rowIndex) : undefined;
                      return <td key={cell.id} rowSpan={rowSpan} style={{ width: cell.column.getSize() }} />;
                    })}
            </RowDragHandleContext.Provider>
          </tr>
        )}
      </Tooltip>
      {isExpandable && row.getIsExpanded() && (
        <tr className={css.expandedContentRow}>
          <td className={css.expandedContent} colSpan={row.getAllCells().length}>
            {renderExpandedRow?.(row.original)}
          </td>
        </tr>
      )}
    </>
  );
};
